import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import './Label.css';
import { resolveValueColorFromStops } from './liquidShared';

const Label = ({
  id,
  title,
  transparentBackground,
  min,
  max,
  value,
  color,
  unit,
  alignment,
  showUnit = true,
  draggable,
  decimals,
  colors,
  colorGrading = false,
  changeColorBasedOnValue = false,
  showValueLine = true,
  labelShadowEnabled = false,
  labelShadowBlur = 28,
  labelShadowStrength = 2,
  labelShadowUseValueColor = false,
  labelShadowColor,
  labelShadowChangeColorBasedOnValue = false,
  labelShadowColorGrading = false,
  labelShadowColors
}) => {
  const [cursorState, setCursorState] = useState('idle');
  const containerRef = useRef(null);

  const numericValue = useMemo(() => {
    const raw = value ?? '';
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [value]);

  const minNum = useMemo(() => {
    const parsed = Number(min);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [min]);

  const maxNum = useMemo(() => {
    const parsed = Number(max);
    return Number.isFinite(parsed) ? parsed : 100;
  }, [max]);

  const rangeMin = useMemo(() => Math.min(minNum, maxNum), [minNum, maxNum]);
  const rangeMax = useMemo(() => Math.max(minNum, maxNum), [minNum, maxNum]);

  const boundedNumericValue = useMemo(() => {
    if (numericValue == null || !Number.isFinite(numericValue)) return null;
    return Math.min(rangeMax, Math.max(rangeMin, numericValue));
  }, [numericValue, rangeMin, rangeMax]);

  const formattedValue = useMemo(() => {
    if (boundedNumericValue != null && Number.isFinite(boundedNumericValue) && decimals != null) {
      return Number(boundedNumericValue.toFixed(decimals)).toString();
    }
    return (value ?? '').toString();
  }, [boundedNumericValue, value, decimals]);

  const resolveColorByValue = useCallback(({
    enabled,
    stops,
    baseColor,
    grading
  }) => {
    return resolveValueColorFromStops({
      value: numericValue,
      baseColor: baseColor || 'var(--label-value-fallback, var(--accent))',
      stops,
      enabled,
      grading,
    }) || baseColor || 'var(--label-value-fallback, var(--accent))';
  }, [numericValue]);

  const activeColor = useMemo(() => resolveColorByValue({
    enabled: changeColorBasedOnValue,
    stops: colors,
    baseColor: color,
    grading: colorGrading
  }), [resolveColorByValue, changeColorBasedOnValue, colors, color, colorGrading]);

  const shadowDynamicColor = useMemo(() => resolveColorByValue({
    enabled: labelShadowChangeColorBasedOnValue,
    stops: labelShadowColors,
    baseColor: labelShadowColor || color,
    grading: labelShadowColorGrading
  }), [
    resolveColorByValue,
    labelShadowChangeColorBasedOnValue,
    labelShadowColors,
    labelShadowColor,
    color,
    labelShadowColorGrading
  ]);

  const shadowUsesValueColor = labelShadowUseValueColor === true;

  const shadowColor = useMemo(() => {
    if (shadowUsesValueColor) {
      return activeColor || color || 'var(--label-value-fallback, var(--accent))';
    }

    if (labelShadowChangeColorBasedOnValue) {
      return shadowDynamicColor || labelShadowColor || color || 'var(--label-value-fallback, var(--accent))';
    }

    if (typeof labelShadowColor === 'string' && labelShadowColor.trim() !== '') {
      return labelShadowColor;
    }

    return activeColor || color || 'var(--label-value-fallback, var(--accent))';
  }, [
    shadowUsesValueColor,
    labelShadowChangeColorBasedOnValue,
    shadowDynamicColor,
    labelShadowColor,
    activeColor,
    color
  ]);

  const parseShadowColorRgb = useCallback((input) => {
    if (typeof input !== 'string') return null;
    const c = input.trim();
    if (!c) return null;

    if (c.startsWith('#')) {
      if (c.length === 4) {
        const r = parseInt(c[1] + c[1], 16);
        const g = parseInt(c[2] + c[2], 16);
        const b = parseInt(c[3] + c[3], 16);
        if ([r, g, b].every(Number.isFinite)) return [r, g, b];
        return null;
      }

      if (c.length === 7) {
        const r = parseInt(c.slice(1, 3), 16);
        const g = parseInt(c.slice(3, 5), 16);
        const b = parseInt(c.slice(5, 7), 16);
        if ([r, g, b].every(Number.isFinite)) return [r, g, b];
        return null;
      }
    }

    const rgbMatch = c.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+)\s*)?\)$/i);
    if (rgbMatch) {
      const clamp = (n) => Math.max(0, Math.min(255, Math.round(Number(n))));
      const r = clamp(rgbMatch[1]);
      const g = clamp(rgbMatch[2]);
      const b = clamp(rgbMatch[3]);
      if ([r, g, b].every(Number.isFinite)) return [r, g, b];
    }

    return null;
  }, []);

  const shadowColorWithAlpha = useCallback((alpha) => {
    const safeAlpha = Math.max(0, Math.min(1, Number(alpha)));
    const rgb = parseShadowColorRgb(shadowColor);
    if (!rgb) return safeAlpha <= 0 ? 'rgba(0, 0, 0, 0)' : shadowColor;
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${safeAlpha})`;
  }, [parseShadowColorRgb, shadowColor]);

  const shadowBlurPx = useMemo(() => {
    const parsed = Number(labelShadowBlur);
    if (!Number.isFinite(parsed)) return 28;
    // Keep blur bounded to avoid expensive paint costs and visual flooding.
    return Math.min(120, Math.max(0, parsed));
  }, [labelShadowBlur]);

  const shadowStrength = useMemo(() => {
    const parsed = Number(labelShadowStrength);
    if (!Number.isFinite(parsed)) return 2;
    return Math.max(0.1, parsed);
  }, [labelShadowStrength]);

  const valueTextShadow = useMemo(() => {
    if (!labelShadowEnabled) return null;

    const strength = Math.max(0.1, Math.min(8, shadowStrength));
    const blur = Math.max(0, shadowBlurPx);

    const innerBlur = Math.max(1, Math.round(Math.min(blur, 24) * 0.45 + strength * 1.5));
    const midBlur = Math.max(innerBlur + 2, Math.round(blur * 0.85 + strength * 4));
    const outerBlur = Math.max(midBlur + 4, Math.round(blur * 1.3 + strength * 7));

    return [
      `0 0 ${innerBlur}px ${shadowColorWithAlpha(Math.max(0.35, Math.min(1, 0.6 + strength * 0.08)))}`,
      `0 0 ${midBlur}px ${shadowColorWithAlpha(Math.max(0.22, Math.min(0.9, 0.36 + strength * 0.06)))}`,
      `0 0 ${outerBlur}px ${shadowColorWithAlpha(Math.max(0.12, Math.min(0.7, 0.2 + strength * 0.05)))}`
    ].join(', ');
  }, [labelShadowEnabled, shadowStrength, shadowBlurPx, shadowColorWithAlpha]);

  const valueBackgroundGlow = useMemo(() => {
    if (!labelShadowEnabled) return null;

    const strength = Math.max(0.1, Math.min(8, shadowStrength));
    const blur = Math.max(0, shadowBlurPx);

    return {
      size: `${Math.round(Math.min(190, 44 + blur * 1.35 + strength * 18))}px`,
      opacity: String(Math.max(0.3, Math.min(0.92, 0.48 + strength * 0.06))),
      core: shadowColorWithAlpha(Math.max(0.18, Math.min(0.5, 0.18 + strength * 0.04))),
      mid: shadowColorWithAlpha(Math.max(0.08, Math.min(0.24, 0.08 + strength * 0.02))),
      edge: shadowColorWithAlpha(Math.max(0.02, Math.min(0.12, 0.03 + strength * 0.012))),
    };
  }, [labelShadowEnabled, shadowStrength, shadowBlurPx, shadowColorWithAlpha]);

  const valueJustifyContent = useMemo(() => {
    const a = String(alignment || 'left').toLowerCase();
    if (a === 'center') return 'center';
    if (a === 'end' || a === 'right') return 'flex-end';
    return 'flex-start';
  }, [alignment]);

  const safeNumeric = (boundedNumericValue != null && Number.isFinite(boundedNumericValue)) ? boundedNumericValue : rangeMin;
  const denom = (rangeMax - rangeMin) || 1;
  const levelPercentage = Math.min(100, Math.max(0, (100 * (safeNumeric - rangeMin) / denom)));

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.style.setProperty('--label-active-color', String(activeColor));
    containerRef.current.style.setProperty(
      '--label-text-align',
      (alignment != null && String(alignment).trim() !== '') ? String(alignment) : 'left'
    );
    containerRef.current.style.setProperty('--label-value-shadow', valueTextShadow || 'none');
    if (valueBackgroundGlow) {
      containerRef.current.style.setProperty('--label-glow-size', valueBackgroundGlow.size);
      containerRef.current.style.setProperty('--label-glow-opacity', valueBackgroundGlow.opacity);
      containerRef.current.style.setProperty('--label-glow-core', valueBackgroundGlow.core);
      containerRef.current.style.setProperty('--label-glow-mid', valueBackgroundGlow.mid);
      containerRef.current.style.setProperty('--label-glow-edge', valueBackgroundGlow.edge);
    } else {
      containerRef.current.style.removeProperty('--label-glow-size');
      containerRef.current.style.removeProperty('--label-glow-opacity');
      containerRef.current.style.removeProperty('--label-glow-core');
      containerRef.current.style.removeProperty('--label-glow-mid');
      containerRef.current.style.removeProperty('--label-glow-edge');
    }
  }, [activeColor, alignment, valueTextShadow, valueBackgroundGlow]);

  const isActionArea = (target) => {
    if (!target || !(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        [
          'button',
          'a',
          'input',
          'select',
          'textarea',
          '[role="button"]',
          '[role="menuitem"]',
          '[data-no-drag]',
          '.widget-actions',
          '.widget-actions *',
          '.widget__actions',
          '.widget__actions *',
          '.actions',
          '.actions *',
          '.toolbar',
          '.toolbar *',
          '.MuiIconButton-root',
          '.MuiButtonBase-root'
        ].join(',')
      )
    );
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'label-widget--grabbing' : 'label-widget--grab')
    : 'label-widget--auto';

  return (
    <div
      ref={containerRef}
      className={[
        "widget-container",
        "label-widget",
        cursorClass,
        transparentBackground ? "label-widget--transparent" : "",
      ].filter(Boolean).join(" ")}
      onMouseDown={(e) => {
        if (!draggable) return;
        if (isActionArea(e.target)) return;
        setCursorState('grabbing');
      }}
      onMouseUp={(e) => {
        if (!draggable) return;
        if (isActionArea(e.target)) return;
        setCursorState('grab');
      }}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        if (isActionArea(e.target)) {
          e.preventDefault();
          return;
        }

        const widgetWrapper = e.target.closest('.widget');
        const widgetId = id || (widgetWrapper ? widgetWrapper.id : '');
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        let dragData = `label;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {showValueLine !== false ? (
        <div
          className="label-widget__level"
          style={{
            backgroundImage: `linear-gradient(to top, ${activeColor}, ${activeColor})`,
            backgroundSize: `100% ${levelPercentage}%`,
            opacity: 0.1,
          }}
        />
      ) : null}

      <h3 className="label-widget__title">{title}</h3>

      <div className="label-widget__valueCluster" style={{ justifyContent: valueJustifyContent }}>
        <span className="label-widget__valueStack">
          {labelShadowEnabled ? (
            <span className="label-widget__valueGlow" aria-hidden="true" />
          ) : null}
          <p className="label-widget__value">
            {formattedValue}
            {showUnit && unit != null && String(unit).trim() !== '' ? (
              <>
                {' '}
                <sup className="label-widget__unit">{unit}</sup>
              </>
            ) : null}
          </p>
        </span>
      </div>
    </div>
  );
};

export default Label;
